import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchMe } from '../../store/slices/authSlice';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, token } = useAppSelector((state) => state.auth);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    // Fetch user info if we have a token
    // This runs after login (when isAuthenticated might be true from login action)
    // or on page refresh (when we have token but need to verify)
    if (token && !isChecking) {
      // If already authenticated, we still want to fetch user info to populate user data
      // But don't block rendering if we're already authenticated
      if (!isAuthenticated) {
        // Not authenticated yet - must fetch user info
        setIsChecking(true);
        dispatch(fetchMe())
          .then(() => {
            setIsChecking(false);
          })
          .catch(() => {
            setIsChecking(false);
            // fetchMe failed - token might be invalid
            // authSlice will handle clearing token
          });
      } else {
        // Already authenticated but might not have user data - fetch it silently
        dispatch(fetchMe()).catch(() => {
          // Silently fail - user data fetch failed but we're already authenticated
        });
      }
    }
  }, [token, isAuthenticated, dispatch, isChecking]);

  // No token - redirect to login
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Have token but not authenticated yet - show loading
  if (!isAuthenticated) {
      return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" tip="Verifying..." />
      </div>
    );
  }

  // Authenticated - show children
  return <>{children}</>;
};

export default PrivateRoute;
